import React, { useState, useEffect, useRef } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  addDoc,
  serverTimestamp,
  doc,
  updateDoc,
  Timestamp,
  onSnapshot,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { sendTextMessage } from '../Helpers';
import { getStaffSMSList, getStaffUsers } from '../helpers/client';

const isStaffId = (id) => id?.toString().endsWith('STAFF');
const extractPhone = (id) => {
  if (!id) return '';
  if (isStaffId(id)) return id.replace(/STAFF$/, '');
  return id;
};

const UnifiedChatbox = ({ fromUser, toUser }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [staffUsers, setStaffUsers] = useState([]);
  const [isClientBlocked, setIsClientBlocked] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);

  const [canSend, setCanSend] = useState(true);
  const [cannotSendReason, setCannotSendReason] = useState('');

  const [attachment, setAttachment] = useState(null);

  const chatContainerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Compute identities
  const computeFromTo = (sender, receiver) => {
    if (!isStaffId(sender.id) && receiver.id === 'JMJ') {
      return { from: extractPhone(sender.id), to: 'JMJ', client: sender.id };
    }
    if (sender.id === 'JMJ' && !isStaffId(receiver.id)) {
      return { from: 'JMJ', to: extractPhone(receiver.id), client: receiver.id };
    }
    if (isStaffId(sender.id) && isStaffId(receiver.id)) {
      return { from: sender.id, to: receiver.id, client: receiver.id };
    }
    return { from: sender.id, to: receiver.id, client: receiver.id };
  };

  const { from, to, client: clientField } = computeFromTo(fromUser, toUser);

  const viewerIsStaff = isStaffId(fromUser.id) || fromUser.id === 'JMJ';
  const otherIsClient = !(isStaffId(toUser.id) || toUser.id === 'JMJ');
  const viewerIsClient = !(isStaffId(fromUser.id) || fromUser.id === 'JMJ');
  const otherIsStaff = isStaffId(toUser.id) || toUser.id === 'JMJ';

  const loadMessages = async () => {
    try {
      const messagesRef = collection(db, 'messages');
      const pairs = new Set();
      pairs.add(JSON.stringify({ f: from, t: to }));
      pairs.add(JSON.stringify({ f: to, t: from }));

      const senderIsStaff = viewerIsStaff;
      const receiverIsClient = otherIsClient;
      const senderIsClient = viewerIsClient;
      const receiverIsStaff = otherIsStaff;

      // staff <-> client variants
      if (senderIsStaff && receiverIsClient) {
        pairs.add(JSON.stringify({ f: 'JMJ', t: extractPhone(toUser.id) }));
        pairs.add(JSON.stringify({ f: extractPhone(toUser.id), t: 'JMJ' }));
        if (fromUser.id !== 'JMJ') {
          pairs.add(JSON.stringify({ f: fromUser.id, t: extractPhone(toUser.id) }));
          pairs.add(JSON.stringify({ f: extractPhone(toUser.id), t: fromUser.id }));
        }
      }

      if (senderIsClient && receiverIsStaff) {
        const staffId = toUser.id;
        pairs.add(JSON.stringify({ f: extractPhone(fromUser.id), t: staffId }));
        pairs.add(JSON.stringify({ f: staffId, t: extractPhone(fromUser.id) }));
        pairs.add(JSON.stringify({ f: extractPhone(fromUser.id), t: 'JMJ' }));
        pairs.add(JSON.stringify({ f: 'JMJ', t: extractPhone(fromUser.id) }));
      }

      const allDocs = [];
      const promises = [];

      for (const pairStr of pairs) {
        const { f, t } = JSON.parse(pairStr);
        promises.push(
          getDocs(query(messagesRef, where('from', '==', f), where('to', '==', t)))
        );
      }

      const staffToClientAll =
        senderIsStaff && receiverIsClient
          ? await getDocs(query(messagesRef, where('to', '==', extractPhone(toUser.id))))
          : null;

      const staffToClientAllForClient =
        senderIsClient && receiverIsStaff
          ? await getDocs(query(messagesRef, where('to', '==', extractPhone(fromUser.id))))
          : null;

      const snaps = await Promise.all(promises);

      snaps.forEach((snap) =>
        snap.docs.forEach((d) => allDocs.push({ id: d.id, ...d.data() }))
      );

      if (staffToClientAll) {
        staffToClientAll.docs.forEach((d) => {
          const data = d.data();
          if (isStaffId(data.from) || data.from === 'JMJ') {
            allDocs.push({ id: d.id, ...data });
          }
        });
      }

      if (staffToClientAllForClient) {
        staffToClientAllForClient.docs.forEach((d) => {
          const data = d.data();
          if (isStaffId(data.from) || data.from === 'JMJ') {
            allDocs.push({ id: d.id, ...data });
          }
        });
      }

      const deduped = Array.from(
        allDocs.reduce((map, m) => {
          if (!map.has(m.id)) map.set(m.id, m);
          return map;
        }, new Map()).values()
      );

      const normalized = deduped.map((m) =>
        !m.client && clientField ? { ...m, client: clientField } : m
      );

      setMessages(normalized);
    } catch (err) {
      console.error('Failed to load messages:', err);
    }
  };

  const markAsRead = async () => {
    try {
      const incomingIdentifier = from;
      const unread = messages.filter((m) => m.to === incomingIdentifier && m.read == null);

      if (unread.length) {
        await Promise.all(
          unread.map((m) => updateDoc(doc(db, 'messages', m.id), { read: Timestamp.now() }))
        );
        await loadMessages();
      }
    } catch (e) {
      console.error('Error marking read:', e);
    }
  };

  const renderMessageContent = (text) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = urlRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(<span key={key++}>{text.slice(lastIndex, match.index)}</span>);
      }

      const rawUrl = match[0];
      const href = rawUrl.startsWith('http') ? rawUrl : `https://${rawUrl}`;

      parts.push(
        <a
          key={key++}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 underline break-words"
        >
          {rawUrl}
        </a>
      );

      lastIndex = urlRegex.lastIndex;
    }

    if (lastIndex < text.length) {
      parts.push(<span key={key++}>{text.slice(lastIndex)}</span>);
    }

    return parts;
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setAttachment(file);
  };

  const handleSend = async () => {
    // allow send if we have text OR an attachment
    if ((!newMessage.trim() && !attachment) || !canSend) return;

    // final guard — re-check if this is a client account and blocked before sending
    const isClientAccount = !isStaffId(fromUser.id) && fromUser.id !== 'JMJ';
    if (isClientAccount) {
      try {
        const clientSnap = await getDoc(doc(db, 'clients', fromUser.id));
        if (clientSnap.exists() && clientSnap.data()?.unableToAct) {
          const isEnglish = fromUser.language === 'English';
          setCanSend(false);
          setCannotSendReason(
            isEnglish
              ? 'Messaging is disabled for your account.'
              : 'La mensajería está deshabilitada para su cuenta.'
          );
          return;
        }
      } catch (e) {
        console.error('Blocked-check failed in handleSend:', e);
        // If this fails we just fall through and let the earlier eligibility guard handle UX.
      }
    }

    const trimmed = newMessage.trim();
    const timestamped = serverTimestamp();

    const senderIsClient = !isStaffId(fromUser.id) && toUser.id === 'JMJ';
    const staffToClient =
      (fromUser.id === 'JMJ' || fromUser.id.endsWith('STAFF')) && !isStaffId(toUser.id);
    const staffToStaff = isStaffId(fromUser.id) && isStaffId(toUser.id);

    // check if this is the client's first-ever message (by matching "from")
    let isFirstClientMessage = false;
    if (senderIsClient) {
      try {
        const priorSnap = await getDocs(
          query(collection(db, 'messages'), where('from', '==', from))
        );
        if (priorSnap.empty) {
          isFirstClientMessage = true;
        }
      } catch (e) {
        console.error('Error checking first client message:', e);
      }
    }

    // Upload attachment if present
    let uploadedAttachment = null;
    if (attachment) {
      try {
        const clientIdOrPhone =
          clientField || extractPhone(fromUser.id) || 'unknown-client';
        const path = `chatAttachments/${clientIdOrPhone}/${Date.now()}_${attachment.name}`;

        const fileRef = storageRef(storage, path);
        const snap = await uploadBytes(fileRef, attachment);
        const url = await getDownloadURL(snap.ref);

        uploadedAttachment = {
          url,
          name: attachment.name,
          type: attachment.type,
          size: attachment.size,
          path,
        };
      } catch (e) {
        console.error('Error uploading attachment:', e);
        return;
      }
    }

    const payload = {
      from,
      to,
      content: trimmed, // can be empty string if attachment-only
      sentOn: timestamped,
      read: null,
    };

    if (clientField) payload.client = clientField;
    if (uploadedAttachment) payload.attachment = uploadedAttachment;

    try {
      await addDoc(collection(db, 'messages'), payload);
      setNewMessage('');
      setAttachment(null);
      if (fileInputRef.current) fileInputRef.current.value = '';

      await loadMessages();

      // If this is the client's first-ever message, send them a confirmation SMS
      if (senderIsClient && isFirstClientMessage) {
        const isEnglish = fromUser.language === 'English';
        try {
          await sendTextMessage({
            message: isEnglish
              ? 'JMJ Pregnancy Center has received your message. A staff member will review it shortly.'
              : 'JMJ Pregnancy Center ha recibido su mensaje. Un miembro del personal lo revisará pronto.',
            yourPhoneNumber: '4073059084',
            recipientPhoneNumber: extractPhone(fromUser.id),
            senderName: 'JMJPC',
          });
        } catch (e) {
          console.error('Failed to send first-message confirmation SMS:', e);
        }
      }

      if (senderIsClient) {
        // notify staff
        const groupDoc = await getDoc(doc(db, 'settings', 'SMS_GROUP'));
        const group = groupDoc.exists()
          ? (groupDoc.data()?.group || '').toString().toLowerCase()
          : 'staff';

        let recipients = [];

        if (group === 'staff') {
          const staffUsers = await getStaffSMSList();
          recipients = staffUsers.map((s) => s.phone?.toString()).filter(Boolean);
        } else if (group === 'development') {
          const devDoc = await getDoc(doc(db, 'settings', 'SMS_DEVELOPMENT'));
          const numbers = Array.isArray(devDoc.data()?.numbers)
            ? devDoc.data().numbers
            : [];
          recipients = numbers.map((n) => n.toString());
        }

        recipients = Array.from(new Set(recipients));

        if (recipients.length) {
          const sendPromises = recipients.map((number) =>
            sendTextMessage({
              message: `JMJPC BOOKING: Client ${fromUser.firstName} ${fromUser.lastName} (${extractPhone(
                fromUser.id
              )}) sent: "${trimmed}"`,
              yourPhoneNumber: '4073059084',
              recipientPhoneNumber: number,
              senderName: 'JMJPC',
            })
          );

          // also notify audit #
          if (group === 'staff') {
            sendPromises.push(
              sendTextMessage({
                message: `JMJPC BOOKING: Client ${fromUser.firstName} ${fromUser.lastName} (${extractPhone(
                  fromUser.id
                )}) sent: "${trimmed}"`,
                yourPhoneNumber: '4073059084',
                recipientPhoneNumber: '4073059084',
                senderName: 'JMJPC',
              })
            );
          }

          await Promise.all(sendPromises);
        }
      } else if (staffToClient) {
        // notify client based on their language
        let lang = toUser.language;
        try {
          const snap = await getDoc(doc(db, 'clients', toUser.id));
          if (snap.exists()) lang = snap.data()?.language ?? lang;
        } catch {}

        const s = (lang || '').toLowerCase();
        const spanish = s === 'spanish' || s === 'español';

        await sendTextMessage({
          message: spanish
            ? `${toUser.firstName}, has recibido un nuevo mensaje de JMJ Pregnancy Center. Por favor inicia sesión para verlo.`
            : `${toUser.firstName}, you have received a new message from JMJ Pregnancy Center. Please log in to view it.`,
          yourPhoneNumber: '4073059084',
          recipientPhoneNumber: extractPhone(toUser.id),
          senderName: 'JMJPC',
        });
      } else if (staffToStaff) {
        // notify staff
        try {
          const snap = await getDoc(doc(db, 'clients', toUser.id));
          const recipientPhone = snap.exists() ? snap.data()?.phone : null;
          if (recipientPhone) {
            await sendTextMessage({
              message: `JMJPC BOOKING: ${fromUser.firstName} ${fromUser.lastName} sent you: "${trimmed}"`,
              yourPhoneNumber: '4073059084',
              recipientPhoneNumber: recipientPhone.toString(),
              senderName: 'JMJPC',
            });
          }
        } catch {}
      }
    } catch (err) {
      console.error('Error sending message:', err);
    }
  };

  // initial load
  useEffect(() => {
    loadMessages();
  }, [fromUser, toUser]);

  useEffect(() => {
    const messagesRef = collection(db, 'messages');
    const unsub = onSnapshot(messagesRef, loadMessages);
    return () => unsub();
  }, [fromUser, toUser]);

  useEffect(() => {
    if (messages.length) markAsRead();
  }, [messages]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (!(viewerIsStaff && otherIsClient)) return;

    (async () => {
      try {
        const users = await getStaffUsers();
        setStaffUsers(users || []);
      } catch {}
    })();
  }, [fromUser, toUser, viewerIsStaff, otherIsClient]);

  // staff view: load client block status
  useEffect(() => {
    if (!(viewerIsStaff && otherIsClient)) {
      setIsClientBlocked(false);
      return;
    }

    (async () => {
      try {
        const snap = await getDoc(doc(db, 'clients', toUser.id));
        setIsClientBlocked(snap.exists() ? !!snap.data()?.unableToAct : false);
      } catch {
        setIsClientBlocked(false);
      }
    })();
  }, [viewerIsStaff, otherIsClient, toUser.id]);

  // check if client is eligible to send messages
  useEffect(() => {
    const checkEligibility = async () => {
      setCanSend(true);
      setCannotSendReason('');

      const senderIsClientAccount = !isStaffId(fromUser.id) && fromUser.id !== 'JMJ';
      if (!senderIsClientAccount) return;

      const isEnglish = fromUser.language === 'English';

      try {
        const clientSnap = await getDoc(doc(db, 'clients', fromUser.id));

        if (!clientSnap.exists()) {
          setCanSend(false);
          setCannotSendReason(
            isEnglish
              ? 'We could not verify your account. Please call the center.'
              : 'No pudimos verificar su cuenta. Por favor llame al centro.'
          );
          return;
        }

        const clientData = clientSnap.data();

        // blocked?
        if (clientData.unableToAct) {
          setCanSend(false);
          setCannotSendReason(
            isEnglish
              ? 'Messaging is disabled for your account.'
              : 'La mensajería está deshabilitada para su cuenta.'
          );
          return;
        }

        // staff-created clients can message without an appointment
        if (clientData.createdByStaff === true) {
          return;
        }

        // appointment check
        const phone = clientData.phone?.toString() || extractPhone(fromUser.id);

        const apptSnap = await getDocs(
          query(collection(db, 'appointments'), where('client', '==', phone))
        );

        const hasEligibleAppt = apptSnap.docs.some((d) => {
          const s = d.data().status;
          return typeof s === 'number';
        });

        if (!hasEligibleAppt) {
          setCanSend(false);
          setCannotSendReason(
            isEnglish
              ? 'You can send messages after you have booked an appointment.'
              : 'Puede enviar mensajes después de haber reservado una cita.'
          );
          return;
        }
      } catch (e) {
        console.error('Eligibility check failed:', e);
        setCanSend(false);
        setCannotSendReason(
          fromUser.language === 'English'
            ? 'There was a problem checking your messaging eligibility.'
            : 'Hubo un problema al verificar su elegibilidad de mensajes.'
        );
      }
    };

    checkEligibility();
  }, [fromUser]);

  const handleBlockToggle = async () => {
    if (!(viewerIsStaff && otherIsClient)) return;

    const actionLabel = isClientBlocked ? 'unblock' : 'block';
    if (!window.confirm(`Are you sure you want to ${actionLabel} this client?`)) return;

    try {
      setBlockLoading(true);
      await updateDoc(doc(db, 'clients', toUser.id), { unableToAct: !isClientBlocked });
      setIsClientBlocked(!isClientBlocked);
    } catch {
      alert(`Failed to ${actionLabel} client.`);
    } finally {
      setBlockLoading(false);
    }
  };

  const sorted = [...messages].sort((a, b) => {
    const aTs = a.sentOn?.seconds || 0;
    const bTs = b.sentOn?.seconds || 0;
    return aTs - bTs;
  });

  const displayName =
    toUser.id === 'JMJ'
      ? 'JMJ Pregnancy Center'
      : `${toUser.firstName} ${toUser.lastName}`;

  const isMessageFromCurrent = (m) => m.from === from;

  return (
    <div className="bg-JMJ w-[100%] h-[500px] rounded-lg flex flex-col items-center text-white">
      <div
        ref={chatContainerRef}
        className="w-full flex flex-col overflow-y-auto flex-1 px-2 py-1"
      >
        {sorted.length > 0 ? (
          sorted.map((message) => {
            const sent = message.sentOn?.seconds
              ? new Date(message.sentOn.seconds * 1000)
              : new Date();

            const timestamp = `${sent.toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: '2-digit',
            })} ${sent.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: true,
            })}`;

            const isStaffViewingClient = viewerIsStaff && otherIsClient;
            const isFromCurrent =
              isMessageFromCurrent(message) ||
              (isStaffViewingClient && (isStaffId(message.from) || message.from === 'JMJ'));

            let staffSenderLabel = null;
            if (
              isStaffViewingClient &&
              (isStaffId(message.from) || message.from === 'JMJ')
            ) {
              if (message.from === 'JMJ') {
                staffSenderLabel = 'JMJ Pregnancy Center';
              } else {
                const staff = staffUsers.find((u) => u.id === message.from);
                staffSenderLabel = staff
                  ? `${staff.firstName ?? ''} ${staff.lastName ?? ''}`.trim()
                  : message.from;
              }
            }

            return (
              <div
                key={message.id}
                className={`flex items-center text-left ${
                  isFromCurrent ? 'justify-end mr-3' : 'justify-start ml-3'
                }`}
              >
                {!isFromCurrent && (
                  <img
                    src={
                      isStaffId(message.from) || message.from === 'JMJ'
                        ? '/DoctorHeadshot.png'
                        : '/BlankPFP.png'
                    }
                    className="w-8 h-8 rounded-full mr-2"
                    alt="peer"
                  />
                )}

                <div
                  className={`max-w-[75%] break-words rounded-lg p-3 m-2 ${
                    isFromCurrent ? 'bg-white text-black ml-auto' : 'bg-JMJGray text-white'
                  }`}
                >
                  {staffSenderLabel && (
                    <div
                      className={`text-xs font-semibold mb-1 ${
                        isFromCurrent ? 'text-gray-600' : 'text-gray-200'
                      }`}
                    >
                      {staffSenderLabel}
                    </div>
                  )}

                  {/* attachment preview */}
                  {message.attachment && message.attachment.url && (
                    <div className="mb-2">
                      {message.attachment.type?.startsWith('image/') ? (
                        <img
                          src={message.attachment.url}
                          alt={message.attachment.name || 'Attachment'}
                          className="max-h-64 rounded-lg"
                        />
                      ) : (
                        <a
                          href={message.attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`underline break-words ${
                            isFromCurrent ? 'text-blue-600' : 'text-blue-300'
                          }`}
                        >
                          {message.attachment.name || 'Download file'}
                        </a>
                      )}
                    </div>
                  )}

                  {renderMessageContent(message.content)}

                  <div
                    className={`text-xs mt-1 ${
                      isFromCurrent ? 'text-gray-500' : 'text-gray-300'
                    }`}
                  >
                    {timestamp}
                  </div>
                </div>

                {isFromCurrent && (
                  <img
                    src={
                      isStaffId(fromUser.id) || fromUser.id === 'JMJ'
                        ? '/DoctorHeadshot.png'
                        : '/BlankPFP.png'
                    }
                    className="w-8 h-8 rounded-full ml-2"
                    alt="self"
                  />
                )}
              </div>
            );
          })
        ) : (
          <p className="text-white font-bold text-center mt-4">
            {fromUser.language === 'English'
              ? 'No message history.'
              : 'Sin historial de mensajes.'}
          </p>
        )}
      </div>

      {/* If client cannot send, show message instead of textbox */}
      {!canSend ? (
        <div className="w-full px-4 py-3 text-center text-white font-semibold">
          {cannotSendReason}
        </div>
      ) : (
        <div className="flex flex-wrap justify-center items-start w-full gap-2 md:gap-[2%] mt-2 px-2 mb-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={
              fromUser.language === 'English'
                ? 'Type a message...'
                : 'Escribe un mensaje...'
            }
            className="p-2 rounded-lg flex-1 min-w-[40%] text-center text-black"
          />

          {/* Styled Choose File button */}
          <div className="flex flex-col items-center">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              className="bg-white text-JMJ font-bold px-3 py-2 rounded disabled:opacity-60 disabled:cursor-not-allowed text-sm whitespace-nowrap"
            >
              {fromUser.language === 'English' ? 'Choose File' : 'Elegir archivo'}
            </button>
            {attachment && (
              <span className="max-w-[130px] truncate text-[10px] mt-1 text-white text-center">
                {attachment.name}
              </span>
            )}
          </div>

          <button
            onClick={handleSend}
            disabled={(!newMessage.trim() && !attachment) || !canSend}
            className="bg-white text-JMJ font-bold px-4 py-2 rounded disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {fromUser.language === 'English' ? 'Send' : 'Enviar'}
          </button>

          {/* Staff users talking to client users: block/unblock button */}
          {viewerIsStaff && otherIsClient && (
            <button
              onClick={handleBlockToggle}
              disabled={blockLoading}
              className={`px-4 py-2 rounded font-bold ${
                isClientBlocked ? 'bg-green-600' : 'bg-red-600'
              }`}
            >
              {blockLoading
                ? fromUser.language === 'English'
                  ? 'Saving...'
                  : 'Guardando...'
                : isClientBlocked
                ? fromUser.language === 'English'
                  ? 'Unblock'
                  : 'Desbloquear'
                : fromUser.language === 'English'
                ? 'Block'
                : 'Bloquear'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default UnifiedChatbox;
